import React, { Dispatch, SetStateAction, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { setSorting } from '../../../store/membersSlice';
import TableHeader from '../../BaseComponents/TableHeader';
import Loader from '../../BaseComponents/Loader';
import './MembersTable.scss';
import MembersTableRow from './MembersTableRow';
import { 
  getDoctorsMembersList, 
  getNutritionistsMembersList,
  getActiveMembersList,
  getBloodIncompleteBookingMembersList,
  getDisabledMembersList,

  getAllMembersList,
  getFreeMembersList,
  getInChargeAdminList,
  getScanIncompleteBookingMembersList,
} from '../../../api/members';
import { SelectOption } from '../../BaseComponents/Select';
import { ListMembersResponseElement, UserRoleEnum } from '../../../types/apiGeneratedTypes';
import { toast } from 'react-toastify';

const baseHeaderColumns = [
  { title: 'Name', key: 'name', minWidth: '220px', width: '220px', flex: 1, sortable: true, apiKey: 'name' },
  { title: 'Membership status', key: 'status', minWidth: '280px', width: '280px', flex: 1, sortable: true, apiKey: 'membership_status' },
  { title: 'Member ID', key: 'id', minWidth: '144px', width: '144px', flex: 1},
  { title: 'Membership Type', key: 'membership_type', minWidth: '120px', width: '120px', flex: 1 },
  { title: 'Add-ons', key: 'addons', minWidth: '220px', width: '220px', flex: 1 },
  { title: 'Assigned Doctor', key: 'doctor', minWidth: '180px', width: '180px', flex: 1, sortable: true, apiKey: 'assigned_doctor' },
  { title: 'Assigned Nutritionist', key: 'nutritionist', minWidth: '230px', width: '230px', flex: 1, sortable: true, apiKey: 'assigned_nutritionist' },
  { title: 'In charge', key: 'inCharge', minWidth: '200px', width: '200px', flex: 1 },
  {
    title: 'Expiration date',
    key: 'expirationDate',
    minWidth: '112px',
    width: '112px',
    flex: 1
  },
  { title: '', key: 'actions', minWidth: '48px', width: '48px', flex: 1 }
];

const additionalHeaderColumns = [
  { title: 'Email ID', key: 'email', minWidth: '280px', width: '280px', flex: 1, headerPaddingLeft: '40px', headerPaddingRight: '16px' },
  {
    title: 'Subscription date',
    key: 'subscriptionCreationDate',
    minWidth: '220px',
    width: '220px',
    flex: 0,
    sortable: true,
    apiKey: 'subscription_creation_date',
  },
  { title: 'Payment amount', key: 'paymentAmount', minWidth: '140px', width: '140px', flex: 0 },
  { title: 'Number of tests ', key: 'numberOfTestsRequired', minWidth: '180px', width: '180px', flex: 0 },
];

const bodyScanAdditionalHeaderColumns = [
  { title: 'Email ID', key: 'email', minWidth: '280px', width: '280px', flex: 1, headerPaddingLeft: '40px', headerPaddingRight: '16px' },
  {
    title: 'Subscription date',
    key: 'subscriptionCreationDate',
    minWidth: '220px',
    width: '220px',
    flex: 0,
    sortable: true,
    apiKey: 'subscription_creation_date',
  },
  { title: 'Payment amount', key: 'paymentAmount', minWidth: '140px', width: '140px', flex: 0 },
  { title: 'Reason for scan', key: 'reasonForScan', minWidth: '220px', width: '220px', flex: 1 },
  { title: 'Postcode', key: 'postcode', minWidth: '140px', width: '140px', flex: 0 },
  { title: 'Site if selected', key: 'siteAddress', minWidth: '240px', width: '240px', flex: 1 },
  { title: 'Requested date', key: 'requestedDate', minWidth: '180px', width: '180px', flex: 0 },
];

const followupStatusHeaderColumn = {
  title: 'Follow-up status',
  key: 'followupStatus',
  minWidth: '220px',
  width: '220px',
  flex: 0,
};

const followupCommentHeaderColumn = {
  title: 'Followup comment',
  key: 'followupComment',
  minWidth: '240px',
  width: '240px',
  flex: 1,
};

const getHeaderColumns = (
  showAdditionalColumns: boolean,
  showBodyScanExtraColumn: boolean,
  showFollowupStatusColumn: boolean,
) => {
  const memberIdHeaderPadding = showAdditionalColumns ? '16px' : '10px';
  const inChargeHeaderPadding = showAdditionalColumns ? '40px' : '16px';
  const doctorHeaderPadding = showAdditionalColumns ? '28px' : '10px';
  const nutritionistHeaderPadding = showAdditionalColumns ? '28px' : '10px';
  const columnsWithAdjustedInCharge = baseHeaderColumns.map((column) =>
    column.key === 'id'
      ? { ...column, headerPaddingLeft: memberIdHeaderPadding }
      : column.key === 'inCharge'
      ? { ...column, headerPaddingLeft: inChargeHeaderPadding }
      : column.key === 'doctor'
        ? { ...column, headerPaddingLeft: doctorHeaderPadding }
      : column.key === 'nutritionist'
        ? { ...column, headerPaddingLeft: nutritionistHeaderPadding }
        : column
  );

  if (!showAdditionalColumns) {
    return columnsWithAdjustedInCharge;
  }

  const additionalColumns = showBodyScanExtraColumn
    ? bodyScanAdditionalHeaderColumns
    : additionalHeaderColumns;

  const followupColumns = showFollowupStatusColumn
    ? [followupStatusHeaderColumn, followupCommentHeaderColumn]
    : [];

  if (showFollowupStatusColumn) {
    return [
      ...columnsWithAdjustedInCharge.slice(0, 4),
      ...followupColumns,
      ...columnsWithAdjustedInCharge.slice(4, 7),
      ...additionalColumns,
      ...columnsWithAdjustedInCharge.slice(7),
    ];
  }

  return [
    ...columnsWithAdjustedInCharge.slice(0, 7),
    ...additionalColumns,
    ...columnsWithAdjustedInCharge.slice(7),
  ];
};
const PAGE_SIZE = 10;

interface MembersTableProps {
  members: ListMembersResponseElement[] | null;
  setMembers: Dispatch<SetStateAction<ListMembersResponseElement[]>>;
  userRole?: UserRoleEnum;
  currentTab?:
    | 'active'
    | 'disabled'
    | 'free'
    | 'all'
    | 'custom-inqurries-blood'
    | 'custom-inqurries-body-scan';
  onMemberStatusChange?: () => void;
  doctorTab?: 'all-members' | 'my-members';
  statusFilter?: { key: string; value: string };
  inchargeFilter?: SelectOption;
  planFilter?: { key: string; value: string };
  doctorFilter?: SelectOption;
  tagFilter?: SelectOption;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  totalCount?: number | string;
  searchQuery?: string;
}

const MembersTable: React.FC<MembersTableProps> = ({ members, setMembers, userRole, currentTab, onMemberStatusChange, doctorTab, statusFilter, inchargeFilter, planFilter, doctorFilter, tagFilter, subscriptionStartDate, subscriptionEndDate, totalCount: propTotalCount, searchQuery }) => {
  const dispatch = useDispatch();
  const { sortKey, sortOrder } = useSelector((state: RootState) => state.members);
  const { admin: { role: adminRole, is_super_admin } } = useSelector(
    (state: RootState) => state.admin
  );
  const canEditInCharge =
    adminRole === UserRoleEnum.Admin ||
    adminRole === UserRoleEnum.SuperAdmin ||
    is_super_admin === true;
  const [inChargeAdminSelectOptions, setInChargeAdminSelectOptions] = React.useState<SelectOption[]>([]);
  const [inChargeAdminsLoading, setInChargeAdminsLoading] = React.useState(canEditInCharge);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [sortingLoading, setSortingLoading] = React.useState<boolean>(false);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [totalCount, setTotalCount] = React.useState<number>(typeof propTotalCount === 'number' ? propTotalCount : typeof propTotalCount === 'string' ? parseInt(propTotalCount) || 0 : 0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sortedMembers, setSortedMembers] = React.useState<any>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const offsetRef = useRef<number>(0);
  const hasInitializedOffsetRef = useRef<boolean>(false);

  const getMembersForPagination = useCallback(async (offset: number, limit: number, search: string, direction: 'asc' | 'desc', columnKey?: string) => {
    const membershipStatus = statusFilter && statusFilter.key !== 'all' ? statusFilter.key : undefined;
    const inChargeAdminName =
      inchargeFilter && inchargeFilter.key !== 'all'
        ? String(inchargeFilter.value).replace(/\s+/g, ' ').trim()
        : undefined;
    const membershipType =
      currentTab === 'active' && planFilter && planFilter.key !== 'all'
        ? planFilter.key
        : undefined;
    const assignedDoctorId =
      doctorFilter && doctorFilter.key !== 'all'
        ? String(doctorFilter.key).trim()
        : undefined;
    const tagIdFilter =
      tagFilter && tagFilter.key !== 'all'
        ? Number(tagFilter.key)
        : undefined;

    if (userRole === UserRoleEnum.Doctor) {
      const memberStatus = doctorTab === 'my-members' ? 'my_members' : 'all_members';
      return await getDoctorsMembersList(offset, limit, search, 'with_doctor', direction, columnKey, memberStatus);
    } else if (userRole === UserRoleEnum.Nutritionist) {
      return await getNutritionistsMembersList(offset, limit, search, 'with_nutritionist', direction, columnKey);
    } else {
      switch (currentTab) {
        case 'active':
          return await getActiveMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, membershipType, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        case 'disabled':
          return await getDisabledMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);

        case 'free':
          return await getFreeMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        case 'all':
          return await getAllMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        case 'custom-inqurries-blood':
          return await getBloodIncompleteBookingMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        case 'custom-inqurries-body-scan':
          return await getScanIncompleteBookingMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        default:
          return await getActiveMembersList(offset, limit, search, 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, membershipType, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
      }
    }
  }, [statusFilter, inchargeFilter, currentTab, planFilter, doctorFilter, tagFilter, userRole, doctorTab, subscriptionStartDate, subscriptionEndDate]);

  const loadMoreMembers = useCallback(async () => {
    if (loading || !hasMore || sortingLoading) return;
    
    setLoading(true);
    try {
      const limit = PAGE_SIZE;
      const currentOffset = offsetRef.current;
      
      const newMembersList = await getMembersForPagination(
        currentOffset,
        limit,
        searchQuery || "",
        sortOrder,
        sortKey || undefined
      );
      
      if (newMembersList.total_count !== undefined) {
        const count = typeof newMembersList.total_count === 'number' 
          ? newMembersList.total_count 
          : parseInt(String(newMembersList.total_count)) || 0;
        if (count > 0) {
          setTotalCount(count);
        }
      }
      
      if (newMembersList?.members && newMembersList.members.length > 0) {
        setMembers((prev) => [...prev, ...newMembersList.members]);
        offsetRef.current = currentOffset + limit;
        
        const apiTotal = typeof newMembersList.total_count === 'number' 
          ? newMembersList.total_count 
          : typeof newMembersList.total_count === 'string' 
            ? parseInt(newMembersList.total_count) || 0 
            : 0;
        const total = apiTotal || totalCount || offsetRef.current;
        setHasMore(offsetRef.current < total && newMembersList.members.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchMembers error :>> ', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [sortOrder, sortKey, loading, hasMore, sortingLoading, setMembers, getMembersForPagination, totalCount, searchQuery]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || loading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 200; 
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
      const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;
      
      if (isNearBottom || isContentSmallerThanContainer) {
        loadMoreMembers();
      }
    }, 100);
  }, [loading, hasMore, loadMoreMembers]);

  const onSort = async (columnKey: string, direction: 'asc' | 'desc') => {
    if (columnKey === 'id' || columnKey === 'expirationDate') {
      return;
    }
    
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    try {
      setSortingLoading(true);
      offsetRef.current = 0;
      hasInitializedOffsetRef.current = false;
      dispatch(setSorting({ key: columnKey, order: direction }));
      
      const membershipStatus = statusFilter && statusFilter.key !== 'all' ? statusFilter.key : undefined;
      const inChargeAdminName =
        inchargeFilter && inchargeFilter.key !== 'all'
          ? String(inchargeFilter.value).replace(/\s+/g, ' ').trim()
          : undefined;
      const membershipType =
        currentTab === 'active' && planFilter && planFilter.key !== 'all'
          ? planFilter.key
          : undefined;
      const assignedDoctorId =
        doctorFilter && doctorFilter.key !== 'all'
          ? String(doctorFilter.key).trim()
          : undefined;
      const tagIdFilter =
        tagFilter && tagFilter.key !== 'all'
          ? Number(tagFilter.key)
          : undefined;
      const limit = PAGE_SIZE; 
      
      let newMembersList;
      if (userRole === UserRoleEnum.Doctor) {
        const memberStatus = doctorTab === 'my-members' ? 'my_members' : 'all_members';
        newMembersList = await getDoctorsMembersList(0, limit, "", 'with_doctor', direction, columnKey, memberStatus);
      } else if (userRole === UserRoleEnum.Nutritionist) {
        newMembersList = await getNutritionistsMembersList(0, limit, "", 'with_nutritionist', direction, columnKey);
      } else {
        switch (currentTab) {
          case 'active':
            newMembersList = await getActiveMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, membershipType, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;
          case 'disabled':
            newMembersList = await getDisabledMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;

          case 'free':
            newMembersList = await getFreeMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;
          case 'all':
            newMembersList = await getAllMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;
          case 'custom-inqurries-blood':
            newMembersList = await getBloodIncompleteBookingMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;
          case 'custom-inqurries-body-scan':
            newMembersList = await getScanIncompleteBookingMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, undefined, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
            break;
          default:
            newMembersList = await getActiveMembersList(0, limit, "", 'with_doctor_and_nutritionist', direction, columnKey, membershipStatus, inChargeAdminName, membershipType, assignedDoctorId, subscriptionStartDate, subscriptionEndDate, tagIdFilter);
        }
      }
      
      if (newMembersList?.members) {
        setSortedMembers(newMembersList.members);
        setMembers(newMembersList.members);
        offsetRef.current = limit;
        
        if (newMembersList.total_count !== undefined) {
          const count = typeof newMembersList.total_count === 'number' ? newMembersList.total_count : parseInt(String(newMembersList.total_count)) || 0;
          if (count > 0) {
            setTotalCount(count);
          }
          setHasMore(offsetRef.current < count);
        } else {
          setHasMore(newMembersList.members.length >= limit);
        }
      } else {
        setSortedMembers([]);
        setMembers([]);
        offsetRef.current = 0;
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error sorting members:', error);
      toast.error('Failed to sort members. Please try again.');
    } finally {
      setSortingLoading(false);
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop;
        }
      });
    }
  };

 
  useEffect(() => {
    if (propTotalCount !== undefined) {
      const count = typeof propTotalCount === 'number' ? propTotalCount : typeof propTotalCount === 'string' ? parseInt(propTotalCount) || 0 : 0;
      if (count > 0) {
        setTotalCount(count);
      }
    }
  }, [propTotalCount]);

  useEffect(() => {
    if (!canEditInCharge) {
      setInChargeAdminSelectOptions([]);
      setInChargeAdminsLoading(false);
      return;
    }

    let cancelled = false;
    setInChargeAdminsLoading(true);
    (async () => {
      try {
        const res = await getInChargeAdminList();
        if (cancelled) return;
        const opts =
          res?.admins?.map((a) => ({
            key: a.user_id ?? a.id ?? '',
            value: `${a.first_name} ${a.last_name}`.trim(),
          })).filter((o) => o.key) ?? [];
        setInChargeAdminSelectOptions(opts);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          toast.error('Failed to load in charge options.');
        }
      } finally {
        if (!cancelled) {
          setInChargeAdminsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEditInCharge]);

  useEffect(() => {
    offsetRef.current = 0;
    hasInitializedOffsetRef.current = false;
    setSortedMembers([]);
  }, [currentTab, statusFilter?.key, inchargeFilter?.key, planFilter?.key, doctorFilter?.key, tagFilter?.key, subscriptionStartDate, subscriptionEndDate, doctorTab, searchQuery]);

  useEffect(() => {
    if (!sortingLoading && !loading && !hasInitializedOffsetRef.current && offsetRef.current === 0) {
      if (members && members.length > 0 && sortedMembers.length === 0) {
        offsetRef.current = Math.ceil(members.length / PAGE_SIZE) * PAGE_SIZE;
        hasInitializedOffsetRef.current = true;
      } else if (members && members.length === 0) {
        offsetRef.current = 0;
        hasInitializedOffsetRef.current = false;
      }
    }
  }, [members, sortedMembers.length, sortingLoading, loading]);

  
  useEffect(() => {
    if (members && members.length > 0) {
      const currentTotal = totalCount || (typeof propTotalCount === 'number' ? propTotalCount : typeof propTotalCount === 'string' ? parseInt(propTotalCount) || 0 : 0);
      if (currentTotal > 0) {
        setHasMore(members.length < currentTotal);
      } else {
        setHasMore(members.length >= PAGE_SIZE);
      }
    } else {
      setHasMore(false);
    }
  }, [members, totalCount, propTotalCount, searchQuery]);

  useEffect(() => { 
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      if (hasMore && members && members.length > 0) {
        setTimeout(() => handleScroll(), 100);
      }
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, hasMore, members]);

  const displayMembers = sortedMembers.length > 0 ? sortedMembers : members;
  const showAdditionalColumns =
    currentTab === 'active' ||
    currentTab === 'all' ||
    currentTab === 'custom-inqurries-blood' ||
    currentTab === 'custom-inqurries-body-scan'||
    currentTab === 'free';
  const showBodyScanExtraColumn = currentTab === 'custom-inqurries-body-scan';
  const showFollowupStatusColumn =
    currentTab === 'custom-inqurries-blood' || currentTab === 'custom-inqurries-body-scan';
  const headerColumns = getHeaderColumns(
    showAdditionalColumns,
    showBodyScanExtraColumn,
    showFollowupStatusColumn,
  );
  return (
    <div className='scroll-container'>
      <div className='table-container members-table mt-0'>
        <TableHeader headerColumns={headerColumns} gap={'35px'} onSort={onSort}/>
        {!displayMembers || displayMembers.length === 0 ? (
          <div className='empty-members'>
            Currently there are no members assigned to you.
          </div>
        ) : (
          <div className='table-body' ref={scrollContainerRef}>
            {sortingLoading ? (
              <div className='members-table-sorting-overlay' aria-busy='true' aria-live='polite'>
                <Loader height='48px' />
              </div>
            ) : null}
            <div className={`members-table-rows${sortingLoading ? ' is-sorting' : ''}`}>
              {displayMembers.map((member: any) => (
                <MembersTableRow
                  memberData={member}
                  key={member.id}
                  userRole={userRole}
                  currentTab={currentTab}
                  onMemberStatusChange={onMemberStatusChange}
                  inChargeAdminSelectOptions={inChargeAdminSelectOptions}
                  inChargeAdminsLoading={inChargeAdminsLoading}
                />
              ))}
            </div>
            {loading && displayMembers.length > 0 && !sortingLoading ? (
              <div className='members-table-pagination-loader' aria-busy='true' aria-live='polite'>
                <Loader height='56px' />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersTable;
